import { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import { Box, CircularProgress } from '@mui/material';
import dayjs from 'dayjs';

import { DialogUI, FormButtonPanelUI, FormHeaderUI } from '../Interface';
import BatchSnFields from './BatchSnFields';
import BatchSnTabs from './BatchSnTabs';
import batchSnService from '../../service/batchSn.service';
import supportingDocumentTypeService from '../../service/supportingDocumentType.service';
import { cleanValues, generateAutocompleteItems } from '../../utils/helper';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import Yup from '../../utils/yup';
import useMenus from '../../context/MenuContext';

const normalizeBatchSn = (row = {}) => ({
  ...row,
  itemCode: row.itemCode ?? '',
  batchSn: row.batchSn ?? '',
  lot: row.lot ?? '',
  addDate: row.addDate ? dayjs(row.addDate) : null,
  supplierId: row.supplierId ?? '',
  expDate: row.expDate ? dayjs(row.expDate) : null,
  mnfDate: row.mnfDate ? dayjs(row.mnfDate) : null,
  batchAttribute1: row.batchAttribute1 ?? '',
  batchAttribute2: row.batchAttribute2 ?? '',
  comment: row.comment ?? '',
  price: row.price ?? '',
  other: row.other ?? '',
  batchSnAttachment: row.batchSnAttachment ?? [],
});

const validationSchema = Yup.object().shape({
  supplierId: Yup.string().nullable(),
  expDate: Yup.string().nullable(),
  mnfDate: Yup.string().nullable(),
  batchAttribute1: Yup.string().nullable(),
  batchAttribute2: Yup.string().nullable(),
  comment: Yup.string().nullable(),
  price: Yup.number().nullable(),
  other: Yup.string().nullable(),
});

const BatchSnDialog = ({ open, onClose, row }) => {
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const [batchSn, setBatchSn] = useState(normalizeBatchSn());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  const [supportingDocumentTypeOptions, setSupportingDocumentTypeOptions] = useState([]);

  useEffect(() => {
    getMenus();
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('batchSn');
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  useEffect(() => {
    if (!open || !row?.batchSn) return;

    let active = true;
    setViewField(true);
    setBatchSn(normalizeBatchSn(row));
    setLoading(true);

    const filterItems = row.batchSnId
      ? [{ field: 'batchSnId', operator: 'equals', value: row.batchSnId }]
      : [
          { field: 'itemCode', operator: 'equals', value: row.itemCode },
          { field: 'batchSn', operator: 'equals', value: row.batchSn },
        ];

    Promise.all([
      batchSnService.getData({
        filter: { filterModel: { items: filterItems, logicOperator: 'and' } },
        sorting: { sortModel: [] },
        pagination: { paginationModel: { pageSize: 25, page: 0 } },
      }),
      supportingDocumentTypeService.getExternalItems(),
    ])
      .then(([data, supportingDocumentTypes]) => {
        if (!active) return;

        const rows = Array.isArray(data?.rows) ? data.rows : [];
        const selectedBatchSn = row.batchSnId
          ? rows.find((item) => String(item.batchSnId) === String(row.batchSnId))
          : rows.find((item) => item.itemCode === row.itemCode && item.batchSn === row.batchSn);

        if (selectedBatchSn) setBatchSn(normalizeBatchSn(selectedBatchSn));
        setSupportingDocumentTypeOptions(generateAutocompleteItems(supportingDocumentTypes ?? [], 'name', 'id'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, row]);

  const handleSave = async (values) => {
    if (!canEdit || viewField) return;

    const attachments = values.batchSnAttachment ?? [];
    const updatedBatchSn = {
      ...cleanValues(values),
      price: parseFloat(values.price),
      expDate: dayjs(values.expDate).isValid()
        ? dayjs(values.expDate).startOf('day').add(12, 'hour').toISOString()
        : null,
      mnfDate: dayjs(values.mnfDate).isValid()
        ? dayjs(values.mnfDate).startOf('day').add(12, 'hour').toISOString()
        : null,
      existingFiles: attachments
        .filter((attachment) => !(attachment instanceof File))
        .map((attachment) => attachment.name),
      batchSnAttachment: attachments.map((attachment) => ({
        ...(attachment instanceof File ? {} : { id: attachment.id }),
        name: attachment.name,
        supportingDocumentTypeId: attachment.supportingDocumentTypeId ?? null,
      })),
    };

    const formData = new FormData();
    formData.append('data', JSON.stringify(updatedBatchSn));

    attachments.forEach((attachment) => {
      if (attachment instanceof File) formData.append('files', attachment);
    });

    setSaving(true);
    try {
      const data = await batchSnService.updateData(formData);
      setBatchSn(normalizeBatchSn(data));
      setViewField(true);
      addNotification({ content: 'Sikeres módosítás!', type: NotificationType.SUCCESS });
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogUI
      open={open}
      onClose={onClose}
      headerContent="Sarzs adatok"
      maxWidth="xl"
      fullWidth
      closeBtn
      contentStyle={{ paddingBottom: 0 }}
    >
      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <FormHeaderUI modClick={() => setViewField(false)} modDisabled={!viewField || !canEdit || saving} />
          <Formik
            initialValues={batchSn}
            validationSchema={validationSchema}
            validateOnChange={false}
            enableReinitialize
            onSubmit={handleSave}
          >
            {({ resetForm }) => (
              <Form>
                <Box mt="1rem">
                  <BatchSnFields viewField={viewField} />
                  <BatchSnTabs disabled={viewField} supportingDocumentTypeOptions={supportingDocumentTypeOptions} />
                  <FormButtonPanelUI
                    disabled={viewField || saving}
                    onCancelClick={() => {
                      resetForm();
                      setViewField(true);
                    }}
                  />
                </Box>
              </Form>
            )}
          </Formik>
        </>
      )}
    </DialogUI>
  );
};

export default BatchSnDialog;
